import { useCallback } from 'react';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import {
  AutomationLevel,
  AutomationTarget,
  Queue,
} from '@rossum/api-client/queues';
import { SchemaWithEtag } from '../../../../../business/schema/useSchema';
import { usePatchQueue } from '../../../../queues/hooks/usePatchQueue';
import { notify } from '../../../../toaster';
import { useAutomationFlags } from '../../core/hooks/useAutomationFlags';
import { QUERY_KEY_GET_SAVED_AUTOMATION_TARGETS } from '../../core/hooks/useGetSavedAutomationTargets';
import { OptimizationData } from '../../core/types';
import {
  getSchemaThresholdMap,
  QUALITY_SPOT_CHECK_PERCENTAGE,
} from '../../core/utils';
import { getAutomationTargetsToSave } from '../utils';
import { GET_AUTOMATION_PROJECTIONS_QUERY_KEY } from './useGetAutomationProjections';
import { useSaveAutomationTargets } from './useSaveAutomationTargets';

type UseSaveOptimizationHandlersProps = {
  queueSchema: SchemaWithEtag | undefined;
  targetState: OptimizationData | null;
  currentState: OptimizationData | null;
  queue: Queue;
  savedAutomationTargets: AutomationTarget[] | undefined;
  getUserDefinedPrecisionTargets: () => Record<string, string>;
  automationLevel: AutomationLevel;
  onSaveSuccess?: () => void;
};

export const useSaveOptimizationHandlers = ({
  queueSchema,
  targetState,
  currentState,
  queue,
  savedAutomationTargets,
  getUserDefinedPrecisionTargets,
  automationLevel,
  onSaveSuccess,
}: UseSaveOptimizationHandlersProps) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { queueLevelFlags } = useAutomationFlags();

  const isAssistantAlreadyEnabled = queueLevelFlags.isUsingAssistant(queue);

  const { mutate: saveAutomationTargets, isLoading: isSavingAutomationTarget } =
    useSaveAutomationTargets();

  const { mutate: patchQueue, isLoading: isPatchingQueue } = usePatchQueue({
    silent: true,
  });

  const handleSaveAutomationTargets = useCallback(
    (callbacks?: { onSuccess?: () => void; onError?: () => void }) => {
      if (
        !targetState ||
        !currentState ||
        !queueSchema ||
        !savedAutomationTargets
      )
        return;

      const { content } = queueSchema;
      if (!content) return;

      const thresholdMapFromSchema = getSchemaThresholdMap(content);

      const automationTargetsToSave = getAutomationTargetsToSave({
        isSavingForTheFirstTime: savedAutomationTargets.length === 0,
        currentState,
        targetState,
        thresholdMapFromSchema,
        userDefinedPrecisionTargets: getUserDefinedPrecisionTargets(),
        defaultScoreThreshold: queue.defaultScoreThreshold,
      });

      saveAutomationTargets(
        { queueId: queue.id, payloads: automationTargetsToSave },
        {
          onSuccess: () => callbacks?.onSuccess?.(),
          onError: error => {
            window.Sentry?.captureException(
              new Error('Failed to save automation targets'),
              { extra: { error } }
            );
            callbacks?.onError?.();
          },
        }
      );
    },
    [
      currentState,
      queue.defaultScoreThreshold,
      queue.id,
      queueSchema,
      saveAutomationTargets,
      savedAutomationTargets,
      targetState,
      getUserDefinedPrecisionTargets,
    ]
  );

  const getIsSavingDisabled = () => {
    // the user has not chosen a target state nor they changed the automation level
    if (automationLevel === queue.automationLevel && !targetState) return true;

    return false;
  };

  const isSavingDisabled = getIsSavingDisabled();

  const handleSave = useCallback(
    ({
      enableQualitySpotCheck = false,
    }: { enableQualitySpotCheck?: boolean } = {}) => {
      if (isSavingDisabled) return;
      const hasAutomationLevelChanged =
        automationLevel !== queue.automationLevel;
      const hasTargetChanged = targetState !== null;

      const notifySaveSuccess = () =>
        notify.default({
          title: intl.formatMessage({
            id: 'containers.settings.queues.queue.automation.optimizeDrawer.save.success',
          }),
        });

      // Side effects that only make sense when an automation target was saved:
      // refetch the baseline so the chart reflects the saved target after RIR
      // recomputes (RIR may briefly return the previous baseline if its
      // recomputation is still in flight), and let the caller reset target /
      // custom-projections UI state.
      const handleTargetSaveSuccess = () => {
        queryClient.invalidateQueries({
          queryKey: [GET_AUTOMATION_PROJECTIONS_QUERY_KEY],
        });
        onSaveSuccess?.();
      };

      const notifySaveError = () =>
        notify.error({
          title: intl.formatMessage({
            id: 'containers.settings.queues.queue.automation.optimizeDrawer.save.error',
          }),
        });

      const runPatchQueue = ({
        automationAssistantEnabled,
        onError,
        onSuccess,
      }: {
        onSuccess: () => void;
        onError: () => void;
        automationAssistantEnabled?: boolean;
      }) =>
        patchQueue(
          {
            queueId: queue.id,
            payload: {
              automationLevel,
              automationEnabled: automationLevel !== 'never',
              ...(enableQualitySpotCheck
                ? { qualitySpotCheckPercentage: QUALITY_SPOT_CHECK_PERCENTAGE }
                : {}),
              settings: {
                // TODO what if the queue is outdated ?
                ...queue.settings,
                automation: {
                  ...queue.settings.automation,
                  ...(automationAssistantEnabled
                    ? { automationAssistantEnabled }
                    : {}),
                },
              },
            },
          },
          { onError, onSuccess }
        );

      const saveTargets = () =>
        handleSaveAutomationTargets({
          onSuccess: () => {
            handleTargetSaveSuccess();
            notifySaveSuccess();
          },
          onError: notifySaveError,
        });

      if (hasAutomationLevelChanged && !hasTargetChanged) {
        runPatchQueue({
          onSuccess: notifySaveSuccess,
          onError: notifySaveError,
        });
        return;
      }

      // Skip the redundant queue PATCH when only targets changed, the
      // assistant flag is already on and QSC does not need enabling — avoids
      // a needless request and a second toast.
      if (
        !hasAutomationLevelChanged &&
        isAssistantAlreadyEnabled &&
        !enableQualitySpotCheck
      ) {
        saveTargets();
        return;
      }

      // PATCH the queue first so the assistant flag (and QSC, when requested)
      // is enabled before the target save — BE rejects automation/targets
      // POSTs with 400 while the assistant is still off.
      runPatchQueue({
        automationAssistantEnabled: true,
        onSuccess: saveTargets,
        onError: notifySaveError,
      });
    },
    [
      automationLevel,
      handleSaveAutomationTargets,
      intl,
      isSavingDisabled,
      patchQueue,
      queue.automationLevel,
      queue.settings,
      queue.id,
      targetState,
      isAssistantAlreadyEnabled,
      queryClient,
      onSaveSuccess,
    ]
  );

  const handleSwitchBack = useCallback(
    (onSuccess?: () => void) => {
      patchQueue(
        {
          queueId: queue.id,
          payload: {
            // TODO what if the queue is outdated ?
            settings: {
              ...queue.settings,
              automation: {
                ...queue.settings.automation,
                automationAssistantEnabled: false,
              },
            },
          },
        },
        {
          onSuccess: () => {
            onSuccess?.();
            notify.default({
              title: intl.formatMessage({
                id: 'containers.message.queueUpdated',
              }),
            });
          },
          onError: () => {
            notify.error({
              title: intl.formatMessage({
                id: 'containers.message.queueError',
              }),
            });
          },
        }
      );
    },
    [queue.id, patchQueue, queue.settings, intl]
  );

  const isInvalidatingAutomationTargets =
    useIsFetching({
      queryKey: [QUERY_KEY_GET_SAVED_AUTOMATION_TARGETS],
    }) > 0;

  const isSaving = isSavingAutomationTarget || isPatchingQueue;

  return {
    handleSave,
    handleSwitchBack,
    isSaving,
    isInvalidating: isInvalidatingAutomationTargets,
    isSavingDisabled,
  };
};
